import { useState, useEffect } from 'react';
import { Folder, Pencil, Trash2 } from 'lucide-react';
import { useFoldersStore } from '@/store/foldersStore';
import { useAuthStore } from '@/store/authStore';
import { useAccountsStore } from '@/store/accountsStore';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';
import { Folder as FolderType } from '@/types/folder';

interface EditFolderModalProps {
  folder: FolderType;
  isOpen: boolean;
  onClose: () => void;
}

export function EditFolderModal({ folder, isOpen, onClose }: EditFolderModalProps) {
  const { user } = useAuthStore();
  const { accounts } = useAccountsStore();
  const { updateFolder, deleteFolder } = useFoldersStore();
  const [folderName, setFolderName] = useState(folder.name);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>(folder.account_ids);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const sortedAccounts = [...accounts].sort((a, b) => 
    a.name.toLowerCase().localeCompare(b.name.toLowerCase())
  );

  useEffect(() => {
    setFolderName(folder.name);
    setSelectedAccounts(folder.account_ids);
  }, [folder]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !folderName.trim()) return;

    setIsSubmitting(true);
    try {
      await updateFolder(folder.id, {
        name: folderName.trim(),
        account_ids: selectedAccounts,
      });

      toast.success("Folder updated successfully");
      
      onClose();
    } catch (error) {
      console.error('Error updating folder:', error);
      toast.error("Failed to update folder");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!user?.id) return;

    setIsDeleting(true);
    try {
      await deleteFolder(folder.id);
      
      toast.success("Folder deleted successfully");
      
      onClose();
    } catch (error) {
      console.error('Error deleting folder:', error);
      toast.error("Failed to delete folder");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] p-0 bg-white">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle className="text-2xl font-semibold flex items-center gap-2">
            <Folder className="w-6 h-6" />
            Edit Folder
          </DialogTitle>
          <DialogDescription className="text-base">
            Modify the folder name and manage which accounts are included in this folder.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="px-6 pb-6">
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="folderName" className="text-sm font-medium">
                Folder Name
              </Label>
              <Input
                id="folderName"
                placeholder="Enter folder name"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                className="h-11"
                required
              />
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">Select Accounts</Label>
              <Card className="border rounded-lg p-1">
                <ScrollArea className="h-[200px] pr-4">
                  <div className="space-y-1 p-3">
                    {sortedAccounts.map((account) => (
                      <div
                        key={account.id}
                        className="flex items-center space-x-3 rounded-md p-2 hover:bg-muted transition-colors"
                      >
                        <Checkbox
                          id={account.id}
                          checked={selectedAccounts.includes(account.id)}
                          onCheckedChange={(checked) => {
                            setSelectedAccounts(prev =>
                              checked
                                ? [...prev, account.id]
                                : prev.filter(id => id !== account.id)
                            );
                          }}
                          className="h-5 w-5"
                        />
                        <Label
                          htmlFor={account.id}
                          className="cursor-pointer flex-grow text-sm"
                        >
                          {account.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </Card>
            </div>
          </div>

          <div className="flex justify-between mt-8">
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting || isSubmitting}
              className="h-11 gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete Folder
            </Button>
            
            <div className="flex space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting || isDeleting}
                className="h-11"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || isDeleting || !folderName.trim()}
                className="h-11 gap-2"
              >
                <Pencil className="w-4 h-4" />
                Update Folder
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
} 